import { useState } from 'react';
import { Plus, Edit2, Trash2, ExternalLink } from 'lucide-react';
import AdminTabs from '../../components/admin/AdminTabs';
import Modal from '../../components/admin/Modal';
import { useAdminSources, type SourceInput } from '../../hooks/useAdminSources';
import type { SourceDto, SourceType } from '@shared/types';

const TYPE_LABELS: Record<SourceType, string> = {
  rss: 'RSS feed',
  wp_rest: 'WordPress REST API',
  google_news: 'Google News (site: query)',
  html: 'HTML scrape',
};

const EMPTY: SourceInput = {
  name: '',
  type: 'rss',
  url: '',
  configJson: null,
  trustScore: 50,
  isEnabled: true,
};

export default function AdminSources() {
  const { list, create, update, remove } = useAdminSources();
  const [editing, setEditing] = useState<SourceDto | null>(null);
  const [adding, setAdding] = useState(false);

  const showForm = adding || editing !== null;

  const onSave = async (input: SourceInput) => {
    if (editing) await update.mutateAsync({ id: editing.id, ...input });
    else await create.mutateAsync(input);
    setAdding(false);
    setEditing(null);
  };

  const onDelete = async (s: SourceDto) => {
    if (!confirm(`Smazat zdroj "${s.name}"? (Smaže i všechny stažené články.)`)) return;
    await remove.mutateAsync(s.id);
  };

  return (
    <div>
      <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Administrace</h1>
      <AdminTabs />

      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {list.data
            ? `${list.data.length} zdrojů, ${list.data.filter((s) => s.isEnabled).length} aktivních`
            : 'Načítám…'}
        </div>
        <button onClick={() => setAdding(true)} className="btn-primary">
          <Plus className="h-4 w-4" /> Přidat zdroj
        </button>
      </div>

      {list.data && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-3">Název</th>
                <th className="px-4 py-3">Typ</th>
                <th className="px-4 py-3">Trust</th>
                <th className="px-4 py-3">Stav</th>
                <th className="px-4 py-3 text-right">Akce</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {list.data.map((s) => (
                <tr key={s.id} className={s.isEnabled ? '' : 'opacity-50'}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-dark">{s.name}</div>
                    <div className="mt-0.5 truncate text-xs text-gray-500" title={s.url}>
                      {s.url}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{TYPE_LABELS[s.type]}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.trustScore}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() =>
                        update.mutate({
                          id: s.id,
                          name: s.name,
                          type: s.type,
                          url: s.url,
                          configJson: s.configJson,
                          trustScore: s.trustScore,
                          isEnabled: !s.isEnabled,
                        })
                      }
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        s.isEnabled ? 'bg-positive-bg text-positive' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {s.isEnabled ? 'Aktivní' : 'Vypnutý'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {s.type !== 'google_news' && (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-ghost"
                          title="Otevřít zdroj"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      <button onClick={() => setEditing(s)} className="btn-ghost" title="Upravit">
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        className="btn-ghost text-danger hover:text-danger"
                        title="Smazat"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <SourceForm
          initial={editing ? sourceToInput(editing) : EMPTY}
          isNew={editing === null}
          onSave={onSave}
          onCancel={() => {
            setAdding(false);
            setEditing(null);
          }}
          saving={create.isPending || update.isPending}
        />
      )}
    </div>
  );
}

function sourceToInput(s: SourceDto): SourceInput {
  return {
    name: s.name,
    type: s.type,
    url: s.url,
    configJson: s.configJson,
    trustScore: s.trustScore,
    isEnabled: s.isEnabled,
  };
}

function SourceForm({
  initial,
  isNew,
  onSave,
  onCancel,
  saving,
}: {
  initial: SourceInput;
  isNew: boolean;
  onSave: (input: SourceInput) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);

  const submit = async () => {
    if (!form.name.trim() || !form.url.trim()) return;
    await onSave(form);
  };

  return (
    <Modal
      title={isNew ? 'Nový zdroj' : 'Upravit zdroj'}
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} className="btn-ghost" disabled={saving}>
            Zrušit
          </button>
          <button onClick={submit} className="btn-primary" disabled={saving}>
            {saving ? 'Ukládám…' : 'Uložit'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">Název</label>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="AGBI"
          />
        </div>

        <div>
          <label className="label">Typ</label>
          <select
            className="input"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as SourceType })}
          >
            {(Object.entries(TYPE_LABELS) as [SourceType, string][]).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">
            {form.type === 'google_news' ? 'Query string (bez `site:` prefix se nepřidává)' : 'URL'}
          </label>
          <input
            className="input"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder={
              form.type === 'google_news'
                ? 'site:khaleejtimes.com (Dubai OR property)'
                : 'https://example.com/feed/'
            }
          />
        </div>

        <div>
          <label className="label">Config JSON (volitelný — selektory pro html, params pro wp_rest)</label>
          <textarea
            className="input min-h-[60px] font-mono text-xs"
            value={form.configJson ?? ''}
            onChange={(e) => setForm({ ...form, configJson: e.target.value || null })}
            placeholder='{"per_page":20}'
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Trust score (0–100)</label>
            <input
              type="number"
              min={0}
              max={100}
              className="input"
              value={form.trustScore}
              onChange={(e) => setForm({ ...form, trustScore: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="label">Stav</label>
            <label className="mt-2 flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
              />
              <span className="text-sm">Aktivní</span>
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
}
